import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import type { VivestSelectOption } from '@vivest-dev/design-system-web-components';

import { Header } from '../../../shared/components/header/header';
import { PLANOS } from '../../../core/constants/planos';
import { VivestControl } from '../../../core/directives/vivest-control';
import { EnumStatusResponse } from '../../../core/enum/EnumHttpResponse';
import { ExtratoDesligamentoPayload } from '../../../core/models/extrato-desligamento';
import { EmpresaResponse, OptionItem } from '../../../core/models/option-item';
import { HttpClientService } from '../../../shared/services/http-client-service';
import { LoaderService } from '../../../shared/services/loader-service';
import { ToastService } from '../../../shared/services/toast-service';
import { cpfValidator, sanitizarCpfParaPayload } from '../../../core/validators/cpf-validator';

const MENSAGENS_OBRIGATORIO = {
  empresa: 'Selecione uma empresa',
  plano: 'Selecione um plano',
  cpf: 'Informe o CPF',
  matricula: 'Informe a matrícula',
} as const;

const MENSAGENS_OPCAO_INVALIDA = {
  empresa: 'Empresa inválida',
  plano: 'Plano inválido',
} as const;

type CampoFormulario = keyof typeof MENSAGENS_OBRIGATORIO;

/** Recusa valores digitados que não correspondem (por id ou por nome, ignorando maiúsculas/minúsculas) a nenhuma opção disponível. */
function opcaoValidaValidator(obterOpcoesValidas: () => readonly OptionItem[]) {
  return (control: AbstractControl<string>): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    return encontrarOpcao(control.value, obterOpcoesValidas()) ? null : { opcaoInvalida: true };
  };
}

/** Localiza a opção correspondente ao valor digitado, por id ou por nome, ignorando maiúsculas/minúsculas. */
function encontrarOpcao(valorDigitado: string, opcoes: readonly OptionItem[]): OptionItem | undefined {
  const valorNormalizado = normalizar(valorDigitado);
  return opcoes.find(
    (opcao) => normalizar(opcao.id) === valorNormalizado || normalizar(opcao.label) === valorNormalizado
  );
}

function normalizar(valor: string): string {
  return valor.trim().toLocaleLowerCase('pt-BR');
}

@Component({
  selector: 'app-home',
  imports: [Header, ReactiveFormsModule, VivestControl],
  templateUrl: './home-adm.html',
  styleUrl: './home-adm.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeAdm implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly httpClientService = inject(HttpClientService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  private readonly empresas = signal<OptionItem[]>([]);
  private readonly gerando = signal(false);

  protected readonly form = this.formBuilder.nonNullable.group({
    empresa: ['', [Validators.required, opcaoValidaValidator(() => this.empresas())]],
    plano: ['', [Validators.required, opcaoValidaValidator(() => PLANOS)]],
    matricula: ['', [Validators.required, Validators.maxLength(10)]],
    cpf: ['', [Validators.required, cpfValidator]],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  protected readonly formInvalido = computed(() => this.formStatus() !== 'VALID');
  protected readonly desabilitado = computed(() => this.formInvalido() || this.gerando());

  protected readonly empresaOptions = computed(() => this.empresas().map(toSelectOption));
  protected readonly planoOptions: VivestSelectOption[] = [...PLANOS]
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
    .map(toSelectOption);

  ngOnInit(): void {
    void this.carregarEmpresas();
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.show('Revise os campos destacados.', 'warning', 3000);
      return;
    }

    const { empresa, plano, cpf, matricula } = this.form.getRawValue();
    const payload: ExtratoDesligamentoPayload = {
      empresa: Number(encontrarOpcao(empresa, this.empresas())?.id ?? empresa),
      plano: Number(encontrarOpcao(plano, PLANOS)?.id ?? plano),
      matricula,
      cpf: sanitizarCpfParaPayload(cpf),
    };

    this.gerando.set(true);
    this.loaderService.showLoader('Gerando extrato...');

    try {
      const { body } = await this.httpClientService.gerarExtratoDesligamento(payload);

      if (!body?.endereco) {
        this.toastService.show('Não foi possível gerar o extrato.', 'error', 3000);
        return;
      }

      window.open(body.endereco, '_blank', 'noopener');
      await this.baixarArquivo(body.endereco);
    } catch (error) {
      this.toastService.show(mensagemErroExtrato(error), 'error', 3000);
    } finally {
      this.gerando.set(false);
      this.loaderService.hideLoader();
    }
  }

  /** Enter em um campo envia o formulário, exceto quando o select já tratou a tecla. */
  protected onEnter(event: Event): void {
    if (event.defaultPrevented) {
      return;
    }

    void this.onSubmit();
  }

  protected temErro(campo: CampoFormulario): boolean {
    const control = this.form.controls[campo];
    return control.invalid && control.touched;
  }

  protected status(campo: CampoFormulario): 'default' | 'error' {
    return this.temErro(campo) ? 'error' : 'default';
  }

  protected mensagemErro(campo: CampoFormulario): string {
    if (!this.temErro(campo)) {
      return '';
    }

    const control = this.form.controls[campo];

    if (control.hasError('required')) {
      return MENSAGENS_OBRIGATORIO[campo];
    }

    if (control.hasError('opcaoInvalida')) {
      return MENSAGENS_OPCAO_INVALIDA[campo as keyof typeof MENSAGENS_OPCAO_INVALIDA];
    }

    if (control.hasError('cpf')) {
      return 'Informe um CPF válido.';
    }

    if (control.hasError('maxlength')) {
      return 'A matrícula deve ter no máximo 10 caracteres.';
    }

    return 'Verifique o valor informado.';
  }

  /**
   * Baixa o PDF assinado. Como o link aponta para outro domínio (blob storage), o atributo
   * `download` de um `<a>` é ignorado pelo navegador nesse caso — por isso o arquivo é buscado
   * via fetch e um Object URL local (mesma origem) é usado para forçar o download.
   */
  private async baixarArquivo(endereco: string): Promise<void> {
    const resposta = await fetch(endereco);
    const blob = await resposta.blob();
    const objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'extrato-desligamento.pdf';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(objectUrl);
  }

  private async carregarEmpresas(): Promise<void> {
    const campoEmpresa = this.form.controls.empresa;
    campoEmpresa.disable();
    this.loaderService.showLoader('Carregando empresas...');

    try {
      const { status, body } = await this.httpClientService.getCompanies();
      const empresas = toEmpresaOptions((body ?? []) as EmpresaResponse[]);

      if (status === EnumStatusResponse.NoContent || empresas.length === 0) {
        this.toastService.show('Nenhuma empresa disponível para consulta.', 'warning');
        return;
      }

      this.empresas.set(empresas);
      campoEmpresa.enable();
    } catch {
      this.toastService.show('Não foi possível carregar as empresas.', 'error');
    } finally {
      this.loaderService.hideLoader();
    }
  }
}

/** Apenas patrocinadoras (tipoEmpresa.id === 1) aparecem na consulta de extrato. */
const TIPO_EMPRESA_PATROCINADORA = 1;

/** Usa o nome abreviado no select e ordena a lista para facilitar a busca. */
function toEmpresaOptions(empresas: EmpresaResponse[]): OptionItem[] {
  return empresas
    .filter((empresa) => empresa.tipoEmpresa?.id === TIPO_EMPRESA_PATROCINADORA)
    .map(({ id, nomeAbreviado, razaoSocial }) => ({
      id: String(id),
      label: nomeAbreviado?.trim() || razaoSocial,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

function toSelectOption({ id, label }: OptionItem): VivestSelectOption {
  return { value: id, label };
}

const MENSAGEM_ERRO_PADRAO = 'Não foi possível gerar o extrato.';

/** Exibe o "detail" do ProblemDetails retornado pela API; na ausência dele, usa a mensagem padrão. */
function mensagemErroExtrato(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return extrairDetail(error.error) ?? MENSAGEM_ERRO_PADRAO;
  }

  return MENSAGEM_ERRO_PADRAO;
}

function extrairDetail(corpo: unknown): string | undefined {
  if (corpo && typeof corpo === 'object' && 'detail' in corpo) {
    const { detail } = corpo as { detail?: string };
    return detail?.trim() || undefined;
  }

  return undefined;
}
